import { Loader2Icon, Brush, X, AlertTriangle, Copy } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { toast } from 'sonner'
import { useCallback, useRef, useState, useEffect } from 'react'
import Image from 'next/image'
import { useUploadAssets } from '@/hooks/use-upload-assets'
import type { Asset, AssetSource } from '@/lib/types'
import useMedia from 'react-use/lib/useMedia'

const MAX_REFERENCE_DESIGNS = 5

export function SetDesignStyleModal({
  onSave,
  onClose,
  payload,
  isDropdownMode = false,
  onStyleSelect,
}: {
  onSave: (payload: {
    referenceDesignsAssets: Asset[]
    selectedStyle: string
    customStyleDescription: string
  }) => void
  onClose: () => void
  payload: {
    referenceDesignsAssets: Asset[]
    selectedStyle?: string
    customStyleDescription?: string
  }
  isDropdownMode?: boolean
  onStyleSelect?: (styleText: string) => void
}) {
  const referenceDesignInputRef = useRef<HTMLInputElement>(null)

  const [referenceDesignsAssets, setReferenceDesignsAssets] = useState<Asset[]>(
    payload.referenceDesignsAssets || [],
  )
  const [selectedStyle, setSelectedStyle] = useState<string>(
    payload.selectedStyle || '',
  )
  const [customStyleDescription, setCustomStyleDescription] = useState<string>(
    payload.customStyleDescription || '',
  )
  const [showStylesDropdown, setShowStylesDropdown] = useState(false)
  const [dropdownTimeoutId, setDropdownTimeoutId] =
    useState<NodeJS.Timeout | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const [hasUsedPresetStyle, setHasUsedPresetStyle] = useState<boolean>(
    !!payload.selectedStyle,
  )
  const [hasUsedCustomStyle, setHasUsedCustomStyle] = useState<boolean>(
    !!payload.customStyleDescription,
  )

  const t = useTranslations('modals.set-design-style')
  const isMobile = useMedia('(max-width: 640px)', false)

  const referenceUpload = useUploadAssets({ unlimited: true })

  // Auto-dismiss error after 5 seconds
  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => {
        setErrorMessage(null)
      }, 5000)
      return () => clearTimeout(timer)
    }
  }, [errorMessage])

  const handleUploadReferenceDesign = useCallback(
    async (e: any) => {
      const files = Array.from(e.target.files || []) as File[]

      // Check upload limits with detailed error messages
      const currentCount = referenceDesignsAssets.length
      const selectedCount = files.length
      const totalAfterUpload = currentCount + selectedCount

      if (totalAfterUpload > MAX_REFERENCE_DESIGNS) {
        const remaining = Math.max(0, MAX_REFERENCE_DESIGNS - currentCount)
        setErrorMessage(
          t('maximum_reference_error', { MAX_REFERENCE_DESIGNS, remaining }),
        )
        return
      }
      if (files.length === 0) return
      const result = await referenceUpload.uploadAssets(files, {
        assetSource: 'chat',
        assetDir: 'chat-images',
      })
      if (!result.success) {
        toast.error(result.error, { duration: 2000 })
        return
      }
      const newReferenceAssets = [
        ...referenceDesignsAssets,
        ...result.assets.map((item) => ({
          ...item,
          source: 'reference' as AssetSource,
          name: 'Project needs to replicate the design (layout or content) shown in this image',
        })),
      ]
      setReferenceDesignsAssets(newReferenceAssets)

      // Auto save and close in dropdown mode
      if (isDropdownMode) {
        onSave({
          referenceDesignsAssets: newReferenceAssets,
          selectedStyle,
          customStyleDescription,
        })
        onClose()
      }
    },
    [
      referenceUpload.uploadAssets,
      referenceDesignsAssets,
      t,
      isDropdownMode,
      onSave,
      selectedStyle,
      customStyleDescription,
      onClose,
    ],
  )

  const handleSave = useCallback(() => {
    const payload = {
      referenceDesignsAssets,
      selectedStyle,
      customStyleDescription,
    }
    onSave(payload)
    if (isDropdownMode) {
      onClose() // Auto close in dropdown mode
    }
  }, [
    onSave,
    referenceDesignsAssets,
    selectedStyle,
    customStyleDescription,
    isDropdownMode,
    onClose,
  ])

  const changeStatus = useCallback(
    (str: string) => {
      if (referenceUpload.isUploading) {
        return `${str} pointer-events-none opacity-50`
      }
      return str
    },
    [referenceUpload.isUploading],
  )

  // Hover handlers for smooth dropdown interaction
  const handleMouseEnterStyleButton = useCallback(() => {
    if (!isMobile) {
      // Clear any existing timeout
      if (dropdownTimeoutId) {
        clearTimeout(dropdownTimeoutId)
        setDropdownTimeoutId(null)
      }
      setShowStylesDropdown(true)
    }
  }, [isMobile, dropdownTimeoutId])

  const handleMouseLeaveStyleButton = useCallback(() => {
    if (!isMobile) {
      // Add a delay before hiding to allow cursor movement to dropdown
      const timeoutId = setTimeout(() => {
        setShowStylesDropdown(false)
        setDropdownTimeoutId(null)
      }, 300) // 300ms delay
      setDropdownTimeoutId(timeoutId)
    }
  }, [isMobile])

  const handleMouseEnterDropdown = useCallback(() => {
    if (!isMobile) {
      // Clear the timeout when entering dropdown
      if (dropdownTimeoutId) {
        clearTimeout(dropdownTimeoutId)
        setDropdownTimeoutId(null)
      }
      setShowStylesDropdown(true)
    }
  }, [isMobile, dropdownTimeoutId])

  const handleMouseLeaveDropdown = useCallback(() => {
    if (!isMobile) {
      // Hide immediately when leaving dropdown
      setShowStylesDropdown(false)
    }
  }, [isMobile])

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (dropdownTimeoutId) {
        clearTimeout(dropdownTimeoutId)
      }
    }
  }, [dropdownTimeoutId])

  // Render dropdown-style content if in dropdown mode
  if (isDropdownMode) {
    return (
      <>
        {/* Error Banner */}
        {errorMessage && (
          <div className="fixed top-6 left-1/2 -translate-x-1/2 z-[9999] animate-in slide-in-from-top duration-300">
            <div className="bg-gradient-to-br from-[#2F1E30]/95 via-[#3D2A3E] to-[#2F1E30]/95 backdrop-blur-lg rounded-xl px-4 py-3 shadow-lg border border-white/10 max-w-md mx-4 relative">
              <button
                onClick={() => setErrorMessage(null)}
                className="absolute top-2 right-2 text-white/80 hover:text-white transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
              <div className="flex items-center gap-3 pr-6">
                <AlertTriangle className="w-5 h-5 text-white flex-shrink-0" />
                <div>
                  <p className="text-white font-light text-sm whitespace-nowrap">
                    {errorMessage}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="relative">
          <div className="w-fit min-w-40 max-w-80 bg-[#332a1e] backdrop-blur-lg rounded-xl p-1 shadow-2xl border border-white/10">
            {/* Add Reference Design Button */}
            <div className="relative group">
              <div
                className={`w-full flex items-center gap-2 p-1.5 rounded-lg transition-all duration-200 text-white border-none mb-0.5 ${
                  referenceUpload.isUploading ||
                  referenceDesignsAssets.length >= MAX_REFERENCE_DESIGNS
                    ? 'opacity-50 cursor-not-allowed'
                    : 'hover:bg-white/10 cursor-pointer'
                }`}
                onClick={() => {
                  if (
                    !(
                      referenceUpload.isUploading ||
                      referenceDesignsAssets.length >= MAX_REFERENCE_DESIGNS
                    )
                  ) {
                    referenceDesignInputRef.current?.click()
                  }
                }}
              >
                <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                  {referenceUpload.isUploading ? (
                    <Loader2Icon className="w-3 h-3 animate-spin" />
                  ) : (
                    <Image
                      src="/ai-chat/reference-design.svg"
                      alt="Reference Design"
                      width={16}
                      height={16}
                      className="w-3 h-3 opacity-80"
                    />
                  )}
                </div>
                <div className="text-left flex-1">
                  <div className="font-light text-sm text-white opacity-80 whitespace-nowrap">
                    {t('upload_reference_design_title')}
                  </div>
                </div>
                <input
                  ref={referenceDesignInputRef}
                  type="file"
                  className="hidden"
                  accept={'.jpg,.png'}
                  multiple
                  disabled={
                    referenceUpload.isUploading ||
                    referenceDesignsAssets.length >= MAX_REFERENCE_DESIGNS
                  }
                  onChange={handleUploadReferenceDesign}
                />
              </div>

              {/* Tooltip for reference design button */}
              {referenceDesignsAssets.length >= MAX_REFERENCE_DESIGNS ? (
                <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block pointer-events-none z-50">
                  <div className="px-3 py-2 bg-black/80 backdrop-blur-md text-white text-xs rounded whitespace-nowrap opacity-95">
                    {t('reference_upload_limit_error', {
                      MAX_REFERENCE_DESIGNS,
                    })}
                  </div>
                  <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-black/50 opacity-95"></div>
                </div>
              ) : (
                <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block pointer-events-none z-50 tooltip-delay">
                  <div className="px-3 py-2 bg-black/80 backdrop-blur-md text-white text-xs rounded whitespace-nowrap opacity-95">
                    {t('reference_tooltip_text', { MAX_REFERENCE_DESIGNS })}
                  </div>
                  <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-black/50 opacity-95"></div>
                </div>
              )}
            </div>

            {/* Divider */}
            <div className="w-full h-px bg-white/10 my-1"></div>

            {/* Design Style Options - All in main menu */}
            {Object.entries({
              light_minimal: t('styles.light_minimal'),
              dark_minimal: t('styles.dark_minimal'),
              artistic: t('styles.artistic'),
              professional: t('styles.professional'),
              gen_z: t('styles.gen_z'),
            }).map(([key, label], index) => (
              <div
                key={key}
                className="w-full flex items-center gap-2 p-1.5 rounded-lg hover:bg-white/10 transition-all duration-200 text-white border-none cursor-pointer"
                onClick={() => {
                  setHasUsedPresetStyle(true)
                  const styleText = `Use design style: ${label}`
                  onStyleSelect?.(styleText)
                  onClose()
                }}
              >
                <div className="text-left flex-1">
                  <div className="font-light text-sm text-white/80 whitespace-nowrap">
                    {label}
                  </div>
                </div>
              </div>
            ))}

            {/* Clone this website option */}
            <div
              className="w-full flex items-center gap-2 p-1.5 rounded-lg hover:bg-white/10 transition-all duration-200 text-white border-none cursor-pointer"
              onClick={() => {
                const styleText = 'Clone this website: [Paste URL here]'
                onStyleSelect?.(styleText)
                onClose()
              }}
            >
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                <Copy className="w-3 h-3 text-white/80" />
              </div>
              <div className="text-left flex-1">
                <div className="font-light text-sm text-white/80 whitespace-nowrap">
                  {t('clone_website')}
                </div>
                <div className="font-light text-xs text-white/60 whitespace-nowrap">
                  {t('clone_website_subtitle')}
                </div>
              </div>
            </div>

            {/* Divider */}
            <div className="w-full h-px bg-white/10 my-1"></div>

            {/* Add My Own Style Button */}
            <div className="relative group">
              <div
                className="w-full flex items-center gap-2 p-1.5 rounded-lg hover:bg-white/10 transition-all duration-200 text-white border-none cursor-pointer"
                onClick={() => {
                  setHasUsedCustomStyle(true)
                  onStyleSelect?.(t('style_text'))
                  onClose()
                }}
              >
                <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                  <Brush className="w-3 h-3 text-white/80" />
                </div>
                <div className="text-left flex-1">
                  <div className="font-light text-sm text-white/80 whitespace-nowrap">
                    {t('describe_own_style')}
                  </div>
                </div>
              </div>

              {/* Recommendation tooltip when preset style was used */}
              {hasUsedPresetStyle && (
                <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block pointer-events-none z-50">
                  <div className="px-3 py-2 bg-black/80 backdrop-blur-md text-white text-xs rounded opacity-95 max-h-[2.4rem] overflow-hidden text-center leading-tight whitespace-nowrap">
                    {t('style_recommendation')}
                  </div>
                  <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-black/50 opacity-95"></div>
                </div>
              )}
            </div>
          </div>
        </div>
      </>
    )
  }

  // Fallback to dropdown mode for any non-dropdown usage
  return (
    <>
      {/* Error Banner */}
      {errorMessage && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-[9999] animate-in slide-in-from-top duration-300">
          <div className="bg-gradient-to-br from-[#2F1E30]/95 via-[#3D2A3E] to-[#2F1E30]/95 backdrop-blur-lg rounded-xl px-4 py-3 shadow-lg border border-white/10 max-w-md mx-4 relative">
            <button
              onClick={() => setErrorMessage(null)}
              className="absolute top-2 right-2 text-white/80 hover:text-white transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="flex items-center gap-3 pr-6">
              <AlertTriangle className="w-5 h-5 text-white flex-shrink-0" />
              <div>
                <p className="text-white font-light text-sm whitespace-nowrap">
                  {errorMessage}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="relative">
        <div className="w-fit min-w-40 max-w-80 bg-[#332a1e] backdrop-blur-lg rounded-xl p-1 shadow-2xl border border-white/10">
          {/* Add Reference Design Button */}
          <div className="relative group">
            <div
              className={`w-full flex items-center gap-2 p-2 rounded-lg transition-all duration-200 text-white border-none mb-0.5 ${
                referenceUpload.isUploading ||
                referenceDesignsAssets.length >= MAX_REFERENCE_DESIGNS
                  ? 'opacity-50 cursor-not-allowed'
                  : 'hover:bg-white/10 cursor-pointer'
              }`}
              onClick={() => {
                if (
                  !(
                    referenceUpload.isUploading ||
                    referenceDesignsAssets.length >= MAX_REFERENCE_DESIGNS
                  )
                ) {
                  referenceDesignInputRef.current?.click()
                }
              }}
            >
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                {referenceUpload.isUploading ? (
                  <Loader2Icon className="w-3 h-3 animate-spin" />
                ) : (
                  <Image
                    src="/ai-chat/reference-design.svg"
                    alt="Reference Design"
                    width={16}
                    height={16}
                    className="w-3 h-3 opacity-80"
                  />
                )}
              </div>
              <div className="text-left flex-1">
                <div className="font-light text-xs text-white opacity-80 whitespace-nowrap">
                  {t('upload_reference_design_title')}
                </div>
              </div>
              <input
                ref={referenceDesignInputRef}
                type="file"
                className="hidden"
                accept={'.jpg,.png'}
                multiple
                disabled={
                  referenceUpload.isUploading ||
                  referenceDesignsAssets.length >= MAX_REFERENCE_DESIGNS
                }
                onChange={handleUploadReferenceDesign}
              />
            </div>

            {/* Tooltip for reference design button */}
            {referenceDesignsAssets.length >= MAX_REFERENCE_DESIGNS ? (
              <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block pointer-events-none z-50">
                <div className="px-3 py-2 bg-black/80 backdrop-blur-md text-white text-xs rounded whitespace-nowrap opacity-95">
                  {t('reference_upload_limit_error', { MAX_REFERENCE_DESIGNS })}
                </div>
                <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-black/50 opacity-95"></div>
              </div>
            ) : (
              <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block pointer-events-none z-50 tooltip-delay">
                <div className="px-3 py-2 bg-black/80 backdrop-blur-md text-white text-xs rounded whitespace-nowrap opacity-95">
                  {t('reference_tooltip_text', { MAX_REFERENCE_DESIGNS })}
                </div>
                <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-black/50 opacity-95"></div>
              </div>
            )}
          </div>

          {/* Choose Design Style Button */}
          <div className="relative group">
            <div
              className="w-full flex items-center gap-2 p-2 rounded-lg hover:bg-white/10 transition-all duration-200 text-white border-none cursor-pointer"
              onMouseEnter={handleMouseEnterStyleButton}
              onMouseLeave={handleMouseLeaveStyleButton}
              onClick={() =>
                isMobile && setShowStylesDropdown(!showStylesDropdown)
              }
            >
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                <Image
                  src="/ai-chat/styles.svg"
                  alt="Styles"
                  width={16}
                  height={16}
                  className="w-3 h-3 opacity-80"
                />
              </div>
              <div className="text-left flex-1">
                <div className="font-light text-sm text-white/80 whitespace-nowrap">
                  {t('select_style')}
                </div>
              </div>
              <div className="w-3 h-3 flex items-center justify-center">
                <span className="text-xs text-white/80">›</span>
              </div>

              {/* Nested Styles Dropdown */}
              {showStylesDropdown && (
                <div
                  className={`absolute bg-gradient-to-br from-[#2F1E30]/95 via-[#3D2A3E] to-[#2F1E30]/95 backdrop-blur-lg rounded-xl p-1 shadow-2xl border border-white/10 z-10 ${
                    isMobile
                      ? 'bottom-full left-0 mb-1 w-full'
                      : 'left-full -top-1.5 ml-1 w-fit min-w-48 max-w-80'
                  }`}
                  onMouseEnter={handleMouseEnterDropdown}
                  onMouseLeave={handleMouseLeaveDropdown}
                >
                  {Object.entries({
                    light_minimal: t('styles.light_minimal'),
                    dark_minimal: t('styles.dark_minimal'),
                    artistic: t('styles.artistic'),
                    professional: t('styles.professional'),
                    gen_z: t('styles.gen_z'),
                  }).map(([key, label], index) => (
                    <div
                      key={key}
                      className="w-full flex items-center gap-2 p-2 rounded-lg hover:bg-white/10 transition-all duration-200 text-white border-none cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation()
                        setHasUsedPresetStyle(true)
                        const styleText = `Use design style: ${label}`
                        onStyleSelect?.(styleText)
                        onClose()
                      }}
                    >
                      <div className="text-left flex-1">
                        <div className="font-light text-sm text-white/80 whitespace-nowrap">
                          {label}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Recommendation tooltip when custom style was used */}
            {hasUsedCustomStyle && (
              <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block pointer-events-none z-50">
                <div className="px-3 py-2 bg-black/80 backdrop-blur-md text-white text-xs rounded opacity-95 max-h-[2.4rem] overflow-hidden text-center leading-tight whitespace-nowrap">
                  {t('style_recommendation')}
                </div>
                <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-black/50 opacity-95"></div>
              </div>
            )}
          </div>

          {/* Add My Own Style Button */}
          <div className="relative group">
            <div
              className="w-full flex items-center gap-2 p-2 rounded-lg hover:bg-white/10 transition-all duration-200 text-white border-none cursor-pointer"
              onClick={() => {
                setHasUsedCustomStyle(true)
                const styleText =
                  'Use my own design style: [e.g. playful, Gen Z, like Duolingo (edit this text)]'
                onStyleSelect?.(styleText)
                onClose()
              }}
            >
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                <Brush className="w-3 h-3 text-white/80" />
              </div>
              <div className="text-left flex-1">
                <div className="font-light text-sm text-white/80 whitespace-nowrap">
                  {t('describe_own_style')}
                </div>
              </div>
            </div>

            {/* Recommendation tooltip when preset style was used */}
            {hasUsedPresetStyle && (
              <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block pointer-events-none z-50">
                <div className="px-3 py-2 bg-black/80 backdrop-blur-md text-white text-xs rounded opacity-95 max-h-[2.4rem] overflow-hidden text-center leading-tight whitespace-nowrap">
                  {t('style_recommendation')}
                </div>
                <div className="absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-black/50 opacity-95"></div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}
